import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AutoTuneLogo, BaceLogo, TuneyLogo, Ellipse13, Ellipse12, CircleIcon } from '../assets/svgs';

// Image imports
const imgChrisSamuels2021 = '/images/ChrisSamuels2021.png';

// Badge component
function Badge({ children }: { children: React.ReactNode }) {
  return (
    <div className="dark:bg-white/10 bg-black/10 w-fit px-6 py-4 rounded-full">
        <div className="font-mono-recursive md:text-lg">
        {children}
      </div>
    </div>
  );
}

// Experience section component
function ExperienceSection({
  date,
  role,
  company,
  description,
  children,
  delay = 0,
}: {
  date: string;
  role: string;
  company: string;
  description: string;
  children?: React.ReactNode;
  delay?: number;
}) {
  return (
    <motion.div
      className="flex md:flex-row flex-col gap-6 md:gap-0.5 items-start justify-center w-full"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.3}}
      viewport={{ once: true, amount: 0.2 }}
    >
      <div className="md:sticky top-32 md:w-96">
        <Badge>{date}</Badge>
      </div>
      <div className="flex-1 flex flex-col gap-6">
        <div className="flex gap-6 md:items-center">
          <CircleIcon className="w-6 h-6" strokeColor="currentColor" strokeWidth={2} />
          <div className="font-mono-recursive md:text-lg">
            {role}
          </div>
        </div>
        <div className="md:text-xl font-medium">
          {company}
        </div>
        <div className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
          {description}
        </div>
        {children}
      </div>
    </motion.div>
  );
}

const About: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center p-4 md:p-6 space-y-16 md:space-y-32">
      {/* Hero Section */}
      <motion.div
        className="flex flex-col gap-48 items-center justify-center w-full pt-8 max-w-5xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.1 }}
      >
        <div className="md:flex gap-0.5 items-start justify-center w-full">
          {/* Profile Image */}
          <motion.div
            className="flex flex-col gap-2 items-start justify-start w-52 h-52 md:h-96 md:w-96"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2, ease: "easeOut" }}
          >
            <div
              className="bg-center bg-no-repeat bg-cover relative rounded-full size-44 md:size-72"
              style={{ backgroundImage: `url('${imgChrisSamuels2021}')` }}
            >
              <div className="absolute dark:border-zinc-800 border-zinc-200 border-8 md:border-[21px] border-solid inset-0 md:inset-[-21px] pointer-events-none rounded-full" />
            </div>
          </motion.div>

          {/* Content */}
          <div className="flex-1 flex flex-col gap-6">
            <motion.h1 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }} className="md:!leading-tight text-3xl md:text-4xl">
              Christopher Samuels
            </motion.h1>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }} className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              I'm a product designer who's worked with both small startups and large companies, creating tools that are intuitive, scalable, and grounded in real user needs. I specialize in designing complex systems and building high-fidelity, interactive prototypes that help teams align and move fast. I co-created{' '}
              <Link className="inline-flex transition text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline underline-offset-4" to="/bace">
                Bace
              </Link>
              , a voice-to-MIDI app and audio plugin built for real-time music creation, blending my passion for design and audio technology. Outside of product work, I'm a musician and producer, playing in two bands{' '}
              <a
                href="https://open.spotify.com/artist/57VMRkaZ6YZhJmO0ZOL3iv?si=UZg5MvmiTIKAOgv7r_l7Yw"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex transition text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline underline-offset-4"
              >
                Ritual Howls
              </a>
              {' '}&{' '}
              <a
                href="https://open.spotify.com/artist/4xYUhkQrEDVfK3Zq1MAuU1?si=cNZfERk_R9mbNdS8FYQOGA"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex transition text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline underline-offset-4"
              >
                Mission to the Sun
              </a>
              {' '}and collaborating with other artists to produce and mix their music. That hands-on experience helps me design tools that truly resonate with creative users.
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Experience Sections */}
      <div className="flex flex-col gap-16 md:gap-36 items-center justify-start max-w-5xl w-full">
        <ExperienceSection
          date="Jul 2026 - Present"
          role="Product Designer"
          company="AutoTune"
          description="Working with Antares on the future of vocal editing and pitch correction, exploring new ways to make precise, natural-sounding adjustments feel faster and more intuitive. My work focuses on interaction design, complex editing workflows, and creating tools that give musicians deep control without getting in the way of the creative process."
          delay={0.1}
        >
          <div className="bg-zinc-900 h-72 relative rounded-xl w-full">
            <div className="flex flex-row items-center overflow-hidden relative h-full w-full">
              <div className="flex h-72 items-center justify-between px-4 py-12 relative w-full">
                <div className="absolute flex h-[482.772px] items-center justify-center right-[-296.06px] top-12 w-[520.061px]">
                  <div className="flex-none rotate-[30deg]">
                    <div className="h-[316.127px] relative w-[418px]">
                      <div className="absolute inset-[-197.39%_-149.28%]">
                        <Ellipse13
                          className="w-full h-full"
                          idPrefix="autotune-ellipse13"
                          fromColor="#01FFDA"
                          toColor="#00A88F"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute flex h-[482.772px] items-center justify-center left-[-396px] top-[-634.5px] w-[520.061px]">
                  <div className="flex-none rotate-[30deg]">
                    <div className="h-[316.127px] relative w-[418px]">
                      <div className="absolute inset-[-197.39%_-149.28%]">
                        <Ellipse12
                          className="w-full h-full"
                          idPrefix="autotune-ellipse12"
                          fill="#FFF688"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center w-full px-8">
                  <AutoTuneLogo className="w-56 md:w-72 h-auto" />
                </div>
              </div>
            </div>
          </div>
        </ExperienceSection>

        <ExperienceSection
          date="Feb 2025 - Jul 2026"
          role="Product Designer"
          company="Tuney"
          description="At Tuney, I designed experiences for an AI-first music platform that lets artists combine generative music with their own loops, samples, and recordings. My work focused on building intuitive interfaces that made advanced tools like AI-assisted arrangement and dynamic manipulation feel seamless and creative. The goal was always to keep musicians in flow while exploring new possibilities with AI."
          delay={0.2}
        >
          <div className="bg-zinc-900 h-72 relative rounded-xl w-full">
            <div className="flex flex-row items-center overflow-hidden relative h-full w-full">
              <div className="flex h-72 items-center justify-between px-4 py-12 relative w-full">
                <div className="absolute flex h-[482.772px] items-center justify-center right-[-296.06px] top-12 w-[520.061px]">
                  <div className="flex-none rotate-[30deg]">
                    <div className="h-[316.127px] relative w-[418px]">
                      <div className="absolute inset-[-197.39%_-149.28%]">
                        <Ellipse13 className="w-full h-full" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute flex h-[482.772px] items-center justify-center left-[-396px] top-[-634.5px] w-[520.061px]">
                  <div className="flex-none rotate-[30deg]">
                    <div className="h-[316.127px] relative w-[418px]">
                      <div className="absolute inset-[-197.39%_-149.28%]">
                        <Ellipse12 className="w-full h-full" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center size-44 w-full">
                  <TuneyLogo className="w-44 h-44" />
                </div>
              </div>
            </div>
          </div>
        </ExperienceSection>

        <ExperienceSection
          date="Nov 2020 - Present"
          role="Founder / Product Designer"
          company="Bace"
          description="I co-founded Bace, a voice-to-MIDI app and audio plugin that transforms vocal input into percussive MIDI for beat makers. I led product design and UX, collaborating on patented audio technology that enabled expressive, real-time rhythm creation. Today, Bace is used by hundreds of musicians who rely on it as a fast, intuitive way to capture ideas and build beats."
          delay={0.3}
        >
          <div className="bg-zinc-900 h-72 relative rounded-xl w-full">
            <div className="flex flex-row items-center overflow-hidden relative h-full w-full">
              <div className="flex h-72 items-center justify-between px-4 py-12 relative w-full">
                <div className="absolute flex h-[482.772px] items-center justify-center right-[-296.06px] top-12 w-[520.061px]">
                  <div className="flex-none rotate-[30deg]">
                    <div className="h-[316.127px] relative w-[418px]">
                      <div className="absolute inset-[-197.39%_-149.28%]">
                        <Ellipse13
                          className="w-full h-full"
                          idPrefix="bace-ellipse13"
                          fromColor="#FC4A1A"
                          toColor="#F7B733"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute flex h-[482.772px] items-center justify-center left-[-396px] top-[-634.5px] w-[520.061px]">
                  <div className="flex-none rotate-[30deg]">
                    <div className="h-[316.127px] relative w-[418px]">
                      <div className="absolute inset-[-197.39%_-149.28%]">
                        <Ellipse12
                          className="w-full h-full"
                          idPrefix="bace-ellipse12"
                          fill="#F7B733"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center w-full px-8">
                  <BaceLogo className="w-56 md:w-72 h-auto" />
                </div>
              </div>
            </div>
          </div>
        </ExperienceSection>

        {/* <div className="flex gap-2.5 h-16 items-center justify-center px-6 py-3 relative rounded-full w-48">
          <div className="absolute border border-zinc-600 border-solid inset-0 pointer-events-none rounded-full" />
          <div className="font-mono-body text-xl text-white">
            +6 more
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default About;
